package com.steamstreet.vegasful.browser.account.subscriptions

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.graphql.api.InteractInput
import com.steamstreet.vegasful.graphql.api.InteractionType
import com.steamstreet.vegasful.web.ClientController
import com.steamstreet.vegasful.web.EntityViewedController
import com.steamstreet.vegasful.web.MORE_INFO_LINK_ATTRIBUTE
import com.steamstreet.vegasful.web.TICKETS_LINK_ATTRIBUTE
import com.steamstreet.vegasful.web.TICKETS_VENUE_ATTRIBUTE
import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import com.steamstreet.vegasful.web.analytics.AnalyticsSource
import com.steamstreet.vegasful.web.analytics.entityIdOf
import com.steamstreet.vegasful.web.analytics.entityTypeOf
import com.steamstreet.vegasful.web.analytics.pageTypeOf
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.events.MouseEvent

/**
 * How far through the scrollable part of the page the visitor must get before the visit counts as
 * showing additional depth rather than a glance.
 */
private const val DEPTH_SCROLL_FRACTION = 0.5

/**
 * A page with less than this much to scroll is short enough that reaching the bottom says nothing
 * about the visitor's interest.
 */
private const val MINIMUM_SCROLLABLE_PIXELS = 400.0

/**
 * How long a click waits for its interaction to reach the server before navigating away. The
 * request is lost if the page unloads first, so a brief wait buys a far more reliable signal.
 */
private const val NAVIGATION_RECORD_TIMEOUT_MS = 500L

/**
 * Links to these entity types represent following a thread of interest out of the current page.
 * Events are excluded: moving from a listing to an event is the ordinary path through the site and
 * says nothing extra about the page being left.
 */
private val RELATED_ENTITY_PREFIXES = listOf("/performers/", "/venues/", "/tags/")

/**
 * Records engagement with the entity whose page is being shown.
 *
 * The visit itself is sent as a VIEWED interaction on load. A MORE_INFO interaction follows if the
 * visitor shows additional depth — scrolling well into the page, following a link to a related
 * performer, venue or tag, or opening the entity's own site. Ticket links are recorded as TICKETS.
 *
 * Two separate things are reported from each of these points, and they are gated differently. The
 * **interaction** feeds the recommendation profile, needs a user to attach to, and is therefore
 * only sent when somebody is signed in. The **analytics event** is sent for everyone: an events
 * site's signed-in visitors are a small and unusually engaged slice of its traffic, so a view
 * metric restricted to them describes almost nobody. That asymmetry is the whole point of this
 * controller not returning early any more.
 */
class VisitController(val element: Element, private val context: CoroutineScope) : ClientController {
    private val entityPath = element.getAttribute(EntityViewedController.pathAttribute)

    /** The venue this entity belongs to, where the page knew one. See [EntityViewedController]. */
    private val venuePath = element.getAttribute(EntityViewedController.venueAttribute)

    /**
     * Depth is only worth reporting once per page view. The server collapses repeats within a day
     * anyway, so this is about avoiding pointless calls rather than about correctness.
     */
    private var depthRecorded = false

    override suspend fun run() {
        val path = entityPath ?: return

        reportView(path)
        record(InteractionType.VIEWED, path)
        listenForScrollDepth(path)
        listenForRelatedEntityLinks(path)
        listenForMarkedLinks()
    }

    /**
     * Report the view to analytics. Events get `event_viewed`, which the retention gates are
     * written in terms of; every other entity type gets `entity_viewed`.
     */
    private fun reportView(path: String) {
        val type = entityTypeOf(path)
        if (type == "event") {
            Analytics.track(
                AnalyticsEvent.EVENT_VIEWED,
                AnalyticsParam.EVENT_ID to entityIdOf(path),
                AnalyticsParam.VENUE_ID to venueId(),
                AnalyticsParam.SOURCE to Analytics.arrivalSource()
            )
        } else {
            Analytics.track(
                AnalyticsEvent.ENTITY_VIEWED,
                AnalyticsParam.ENTITY_TYPE to type,
                AnalyticsParam.ENTITY_ID to entityIdOf(path),
                AnalyticsParam.SOURCE to Analytics.arrivalSource()
            )
        }
    }

    /**
     * The venue to report against this page: the one the server named, or the entity itself when
     * this *is* a venue page.
     */
    private fun venueId(): String? = when {
        venuePath != null -> entityIdOf(venuePath)
        entityPath != null && entityTypeOf(entityPath) == "venue" -> entityIdOf(entityPath)
        else -> null
    }

    /**
     * Send an interaction. Only signed-in visitors have somewhere for one to be recorded; the
     * analytics event that accompanies each of these is sent separately and unconditionally.
     */
    private suspend fun record(type: InteractionType, path: String) {
        if (!Account.isLoggedIn()) return
        GraphQL.mutation {
            subscriptions {
                this.interact(InteractInput(type, path))
            }
        }
    }

    /**
     * Report that the visitor went deeper into this entity, if that hasn't been reported already.
     * [source] is how they showed it — see [com.steamstreet.vegasful.web.analytics.AnalyticsSource].
     */
    private fun recordDepth(path: String, source: String, sendInteraction: Boolean = true) {
        if (depthRecorded) return
        depthRecorded = true
        Analytics.track(
            AnalyticsEvent.ENTITY_DEPTH_REACHED,
            AnalyticsParam.ENTITY_TYPE to entityTypeOf(path),
            AnalyticsParam.ENTITY_ID to entityIdOf(path),
            AnalyticsParam.SOURCE to source
        )
        if (!sendInteraction) return
        context.launch {
            record(InteractionType.MORE_INFO, path)
        }
    }

    /**
     * Watch for the visitor scrolling well into the page.
     *
     * Reading `scrollHeight` forces the browser to lay the page out, and this runs on every scroll
     * event — which, now that the controller is no longer gated on being signed in, means for
     * essentially all traffic rather than the few percent who are logged in. So the flag is checked
     * before anything is measured: once depth has been reported there is nothing left to work out,
     * and the handler becomes a single boolean test. The listener is `passive` for the same reason,
     * which lets the browser scroll without waiting to see whether this handler cancels it.
     */
    private fun listenForScrollDepth(path: String) {
        window.addEventListener("scroll", {
            if (!depthRecorded) {
                val scrollable = (document.documentElement?.scrollHeight?.toDouble() ?: 0.0) - window.innerHeight
                if (scrollable >= MINIMUM_SCROLLABLE_PIXELS && window.scrollY >= scrollable * DEPTH_SCROLL_FRACTION) {
                    recordDepth(path, AnalyticsSource.SCROLL)
                }
            }
        }, js("({ passive: true })"))
    }

    /**
     * Watch for the visitor following a link to a related entity. This listens on pointer-down so
     * that the request is under way before the browser starts tearing the page down.
     */
    private fun listenForRelatedEntityLinks(path: String) {
        document.addEventListener("pointerdown", { event ->
            val anchor = (event.target as? Element)?.closest("a[href]") as? HTMLAnchorElement
            if (anchor != null &&
                // Site navigation isn't a signal of interest in the entity being viewed.
                anchor.closest("nav, header, footer") == null &&
                RELATED_ENTITY_PREFIXES.any { anchor.pathname.startsWith(it) }
            ) {
                recordDepth(path, AnalyticsSource.RELATED_LINK)
            }
        })
    }

    /**
     * Watch for clicks on links the page has marked as leading to tickets or to further detail.
     */
    private fun listenForMarkedLinks() {
        document.addEventListener("click", { event ->
            val mouse = event as? MouseEvent ?: return@addEventListener
            val target = event.target as? Element ?: return@addEventListener

            val (type, markedPath) = markedInteraction(target) ?: return@addEventListener
            val anchor = target.closest("a[href]") as? HTMLAnchorElement
            val href = anchor?.href

            reportMarkedLink(type, markedPath, target)

            // A modified click, or a link that opens in its own tab, leaves this page intact, so
            // the interaction can be sent without holding anything up. So does a click by
            // somebody who is not signed in, for whom there is no interaction to send at all.
            val leavesPageIntact = !Account.isLoggedIn() ||
                    href == null || anchor.target == "_blank" ||
                    mouse.metaKey || mouse.ctrlKey || mouse.shiftKey || mouse.altKey ||
                    mouse.button.toInt() != 0

            if (leavesPageIntact) {
                context.launch {
                    record(type, markedPath)
                }
            } else {
                // Otherwise this page is about to be unloaded, which would cancel the request.
                // Send it first, then follow the link regardless of how that turned out.
                event.preventDefault()
                context.launch {
                    withTimeoutOrNull(NAVIGATION_RECORD_TIMEOUT_MS) {
                        record(type, markedPath)
                    }
                    window.location.href = href
                }
            }
        })
    }

    /**
     * Report a click on a marked link to analytics.
     *
     * `ticket_link_tapped` is the conversion event, so it carries the three properties the app
     * sends — `event_id`, `venue_id` and `source` — plus the entity type and id, because a ticket
     * link also appears on venue and thing pages where there is no event to name. `event_id` is
     * simply absent there rather than filled in with the wrong thing.
     *
     * A MORE_INFO click is the same signal as scroll depth, so it reports [ENTITY_DEPTH_REACHED]
     * and is subject to the same once-per-page rule.
     */
    private fun reportMarkedLink(type: InteractionType, markedPath: String, target: Element) {
        when (type) {
            InteractionType.TICKETS -> {
                val entityType = entityTypeOf(markedPath)
                Analytics.track(
                    AnalyticsEvent.TICKET_LINK_TAPPED,
                    AnalyticsParam.EVENT_ID to entityIdOf(markedPath).takeIf { entityType == "event" },
                    AnalyticsParam.VENUE_ID to markedVenueId(markedPath, target),
                    AnalyticsParam.ENTITY_TYPE to entityType,
                    AnalyticsParam.ENTITY_ID to entityIdOf(markedPath),
                    AnalyticsParam.SOURCE to pageTypeOf(window.location.pathname)
                )
            }

            // Analytics only: the caller sends this one's interaction itself, and with the
            // navigation hold that a click leaving the page needs.
            InteractionType.MORE_INFO ->
                recordDepth(markedPath, AnalyticsSource.DETAIL_LINK, sendInteraction = false)

            else -> Unit
        }
    }

    /**
     * The venue for a ticket link: the one the link itself was tagged with, the entity when the
     * link points at a venue, or the page's venue as a last resort.
     */
    private fun markedVenueId(markedPath: String, target: Element): String? {
        target.closest("[$TICKETS_VENUE_ATTRIBUTE]")?.getAttribute(TICKETS_VENUE_ATTRIBUTE)?.let {
            return entityIdOf(it)
        }
        if (entityTypeOf(markedPath) == "venue") return entityIdOf(markedPath)
        return venueId()
    }

    /**
     * Find the interaction, if any, that the page has attached to the region around this element.
     */
    private fun markedInteraction(target: Element): Pair<InteractionType, String>? {
        target.closest("[$TICKETS_LINK_ATTRIBUTE]")?.getAttribute(TICKETS_LINK_ATTRIBUTE)?.let {
            return InteractionType.TICKETS to it
        }
        target.closest("[$MORE_INFO_LINK_ATTRIBUTE]")?.getAttribute(MORE_INFO_LINK_ATTRIBUTE)?.let {
            return InteractionType.MORE_INFO to it
        }
        return null
    }
}
